import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { linkHandler } from '../router';

export interface Crumb {
  label: string;
  /** In-app path; the last crumb (the current page) has none. */
  path?: string;
}

interface PageHeaderProps {
  title: React.ReactNode;
  /** Short line under the title (time zone, a description, ...). */
  subtitle?: React.ReactNode;
  /** Breadcrumb trail leading to this page (the page itself is added). */
  crumbs?: Crumb[];
  /** The page's actions, primary action last (rendered top right). */
  actions?: React.ReactNode;
}

/**
 * Breadcrumb, title, subtitle, and the page's actions at the top right, the
 * same on every page (web-frontend.md, Cockpit-inspired page anatomy).
 * Actions wrap under the title on phones.
 */
export function PageHeader({ title, subtitle, crumbs, actions }: PageHeaderProps) {
  return (
    <Stack spacing={1} sx={{ mb: 2 }} data-testid="page-header">
      {crumbs && crumbs.length > 0 && (
        <Breadcrumbs aria-label="Breadcrumb" sx={{ fontSize: 14 }}>
          {crumbs.map((c) =>
            c.path ? (
              <Link
                key={c.label}
                href={c.path}
                onClick={linkHandler(c.path)}
                underline="hover"
                color="inherit"
                sx={{ overflowWrap: 'anywhere' }}
              >
                {c.label}
              </Link>
            ) : (
              <Typography key={c.label} color="text.secondary" sx={{ fontSize: 'inherit' }}>
                {c.label}
              </Typography>
            ),
          )}
          <Typography color="text.primary" sx={{ fontSize: 'inherit' }} aria-current="page">
            {title}
          </Typography>
        </Breadcrumbs>
      )}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        sx={{ justifyContent: 'space-between', alignItems: { sm: 'center' } }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h5" component="h1" sx={{ overflowWrap: 'anywhere' }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography component="div" variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        {actions && (
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1, flexShrink: 0 }}
          >
            {actions}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}
